import * as THREE from 'three'
import Experience from './Experience.js'
import vertexShader from './Shaders/vertex.glsl'
import fragmentShader from './Shaders/fragment.glsl'

export default class Logo {
    constructor() {
        this.experience = new Experience()
        this.scene = this.experience.scene
        this.time = this.experience.time
        this.sizes = this.experience.sizes
        this.camera = this.experience.camera.instance
        
        this.setGeometry()
        this.loadTexture()
        
        // Input tracking (works for both mouse and touch via pointer events)
        this.mouseUV = new THREE.Vector2(0.5, 0.5)
        this.prevMouseUV = new THREE.Vector2(0.5, 0.5)
        this.targetMouseUV = new THREE.Vector2(0.5, 0.5) // For smooth interpolation
        this.inputNDC = new THREE.Vector2(0, 0) // Generic input coordinates
        this.isInputOverMesh = false
        
        this.raycaster = new THREE.Raycaster()
        
        // Add pointermove event listener for all pointer inputs
        window.addEventListener('pointermove', this.onPointerMove.bind(this))
        
        // Set default circular radius (float value, 0.0 to 1.0 range)
        this.mouseRadius = 0.3; // Default radius for circular distortion
        
        this.sizes.on('resize', () => {
            this.updateSize()
        })
    }
    
    setGeometry() {
        this.geometry = new THREE.PlaneGeometry(1, 1)
    }
    
    loadTexture() {
        const textureLoader = new THREE.TextureLoader()
        textureLoader.load(
            'https://cdn.prod.website-files.com/64892891a9277d495f1c6ba2/67c2886d0ec24202bfe0e96f_logo_cw.png',
            // 'https://cdn.prod.website-files.com/64892891a9277d495f1c6ba2/67c452d8c591228c81a01c2a_logo_cw-b.png',
            (texture) => {
                this.texture = texture
                this.setMaterial()
                this.setMesh()
            },
            undefined,
            (error) => {
                console.error('Error loading logo texture:', error)
                textureLoader.load(
                    'https://threejs.org/examples/textures/crate.gif',
                    (texture) => {
                        this.texture = texture
                        this.setMaterial()
                        this.setMesh()
                    }
                )
            }
        )
    }
    
    setMaterial() {
        this.material = new THREE.ShaderMaterial({
            vertexShader: vertexShader,
            fragmentShader: fragmentShader,
            transparent: true,
            uniforms: {
                uTexture: { value: this.texture },
                uEaseFactor: { value: 0.5 }, // Adjusted for smoother easing
                uMousePosition: { value: new THREE.Vector2(0.5, 0.5) },
                uPrevPosition: { value: new THREE.Vector2(0.5, 0.5) },
                uMouseRadius: { value: this.mouseRadius }, // Single float for circular radius
            }
        })
    }
    
    setMesh() {
        this.mesh = new THREE.Mesh(this.geometry, this.material)
        this.scene.add(this.mesh)
        this.updateSize()
    }
    
    updateSize() {
        if (!this.mesh || !this.texture) return
        
        const camera = this.camera
        const distance = Math.abs(camera.position.z)
        const vFov = camera.fov * Math.PI / 180
        const viewportHeight = 2 * Math.tan(vFov / 2) * distance
        const viewportWidth = viewportHeight * camera.aspect
        
        const imageAspect = this.texture.image.width / this.texture.image.height
        const widthPercentage = 0.98
        const width = viewportWidth * widthPercentage
        const height = width / imageAspect
        
        this.mesh.scale.set(width, height, 1)
        
        const bottomPadding = viewportHeight * 0.01
        this.mesh.position.set(
            0,
            -viewportHeight / 2 + height / 2 + bottomPadding,
            0
        )
    }
    
    // Handle pointer movement (mouse, touch, stylus, etc.)
    onPointerMove(event) {
        // Prevent default behavior (e.g., scrolling on touch devices)
        event.preventDefault();
        
        // Use pointer event coordinates
        this.inputNDC.x = (event.clientX / this.sizes.width) * 2 - 1;
        this.inputNDC.y = -(event.clientY / this.sizes.height) * 2 + 1;
        
        if (!this.mesh || !this.camera) return;
        
        this.raycaster.setFromCamera(this.inputNDC, this.camera);
        const intersects = this.raycaster.intersectObject(this.mesh);
        
        if (intersects.length > 0) {
            this.isInputOverMesh = true;
            const uv = intersects[0].uv;
            this.targetMouseUV.set(uv.x, uv.y);
        } else {
            this.isInputOverMesh = false;
            // Optionally reset to center when pointer leaves, or maintain last position
            // this.targetMouseUV.set(0.5, 0.5);
        }
    }
    
    update() {
        if (!this.mesh || !this.material) return;
        
        // Smoothly interpolate input position
        this.mouseUV.lerp(this.targetMouseUV, this.material.uniforms.uEaseFactor.value);
        
        // Update shader uniforms
        this.material.uniforms.uPrevPosition.value.copy(this.prevMouseUV); // Previous frame's position
        this.material.uniforms.uMousePosition.value.copy(this.mouseUV);    // Current interpolated position
        this.material.uniforms.uMouseRadius.value = this.mouseRadius;     // Update circular radius
        
        // Store current position as previous for next frame
        this.prevMouseUV.copy(this.mouseUV);
        
        // Ensure material updates
        this.material.needsUpdate = true;
    }

    // Optional: Method to adjust uMouseRadius dynamically (e.g., via UI or input)
    setMouseRadius(radius) {
        this.mouseRadius = Math.max(0.0, Math.min(1.0, radius)); // Clamp between 0 and 1
        if (this.material) {
            this.material.uniforms.uMouseRadius.value = this.mouseRadius;
        }
    }
}